import { isNamedProvider, type SocialProvider } from '../social/outcome';

/**
 * Everything the multi-step signup has to survive a refresh with, and nothing else.
 *
 * It lives in `sessionStorage` so a reload lands back on the same step while the code or the
 * proof is still alive, and dies with the tab. The password is deliberately absent from the
 * shape: it is typed on the details step and submitted from React state, never written down.
 */

const SIGNUP_KEY = 'vadio:signup';

export interface SignupDraftDetails {
	firstName: string;
	lastName: string;
	phone: string;
}

/** What a proof-before-link screen needs to repeat the exact same provider exchange. */
export interface LinkProofHandoff {
	email: string;
	provider: SocialProvider;
	idToken: string;
	nonce: string;
}

export interface SignupSessionSnapshot {
	/** The address a code was last sent to, so a refresh on step two knows the code is live. */
	codeSentFor: string | null;
	/** When the resend link becomes clickable again. */
	resendAvailableAt: string | null;
	proofToken: string | null;
	proofExpiresAt: string | null;
	/** The details already typed, kept so a proof timeout loses nothing. */
	details: SignupDraftDetails | null;
	linkProof: LinkProofHandoff | null;
	/** The address the link-proof code was sent to, so a refresh does not send a second one. */
	linkProofCodeSentFor: string | null;
}

export const emptySignupSession: SignupSessionSnapshot = {
	codeSentFor: null,
	resendAvailableAt: null,
	proofToken: null,
	proofExpiresAt: null,
	details: null,
	linkProof: null,
	linkProofCodeSentFor: null,
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const readString = ({ source, key }: { source: Record<string, unknown>; key: string }): string | null => {
	const value = source[key];
	return typeof value === 'string' && value.length > 0 ? value : null;
};

const parseDetails = (value: unknown): SignupDraftDetails | null => {
	if (!isRecord(value)) return null;
	const firstName = value.firstName;
	const lastName = value.lastName;
	const phone = value.phone;
	if (typeof firstName !== 'string' || typeof lastName !== 'string' || typeof phone !== 'string') return null;
	return { firstName, lastName, phone };
};

const parseLinkProof = (value: unknown): LinkProofHandoff | null => {
	if (!isRecord(value)) return null;
	const email = readString({ source: value, key: 'email' });
	const idToken = readString({ source: value, key: 'idToken' });
	const nonce = readString({ source: value, key: 'nonce' });
	const provider = value.provider;
	if (!email || !idToken || !nonce) return null;
	// The closed set, never a hand-copied list: a provider added to `SOCIAL_PROVIDERS` round-trips
	// through storage without anyone remembering to widen this check.
	if (typeof provider !== 'string' || !isNamedProvider(provider)) return null;
	return { email, idToken, nonce, provider };
};

/** Rebuilds the snapshot from storage, dropping anything that is not the shape we wrote. */
export const parseSignupSession = (raw: string | null): SignupSessionSnapshot => {
	if (!raw) return emptySignupSession;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return emptySignupSession;
	}
	if (!isRecord(parsed)) return emptySignupSession;

	return {
		codeSentFor: readString({ source: parsed, key: 'codeSentFor' }),
		resendAvailableAt: readString({ source: parsed, key: 'resendAvailableAt' }),
		proofToken: readString({ source: parsed, key: 'proofToken' }),
		proofExpiresAt: readString({ source: parsed, key: 'proofExpiresAt' }),
		details: parseDetails(parsed.details),
		linkProof: parseLinkProof(parsed.linkProof),
		linkProofCodeSentFor: readString({ source: parsed, key: 'linkProofCodeSentFor' }),
	};
};

const storage = (): Storage | null => {
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
};

export const readSignupSession = (): SignupSessionSnapshot => {
	const store = storage();
	if (!store) return emptySignupSession;
	try {
		return parseSignupSession(store.getItem(SIGNUP_KEY));
	} catch {
		return emptySignupSession;
	}
};

export const writeSignupSession = (patch: Partial<SignupSessionSnapshot>): SignupSessionSnapshot => {
	const next = { ...readSignupSession(), ...patch };
	const store = storage();
	try {
		store?.setItem(SIGNUP_KEY, JSON.stringify(next));
	} catch {
		// A blocked or full sessionStorage costs the refresh-resumption, not the flow.
	}
	return next;
};

export const clearSignupSession = (): void => {
	const store = storage();
	try {
		store?.removeItem(SIGNUP_KEY);
	} catch {
		// Nothing to do: the tab is closing on it anyway.
	}
};
